import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Route, Switch, Redirect } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Typography, Paper } from "@material-ui/core";
import { connect } from "react-redux";
import NavBar from "features/navigation/components/navbar/navbar-container";
import StaffRoutesContainer from "features/staff/components/staff-routes-container";
import JobsRoutesContainer from "features/jobs/components/jobs-routes-container";
import { AccountRoutes } from "features/account/components/account-routes";
import { ACCOUNT_ROUTE, ACCOUNT_SUBSCRIPTION_ROUTE } from "features/account";
import { AdminHome } from "features/home/components/admin-home";
import { SuperAdminDashboard } from "features/home/components/super-admin-dashboard";
import { InfoRoutes } from "features/home/components/info-routes";
import { TaskRoutes } from "features/home/components/tasks-routes";
import { HomeRoutes } from "features/home/components/home-routes";
import {
	HOME_ROUTE,
	INFO_HOME_ROUTE,
	TASK_HOME_ROUTE,
	ADMIN_HOME_ROUTE,
	SUPER_ADMIN_DASHBOARD_ROUTE
} from "features/home/home-routes";
import { CustomerRoutes, CUSTOMERS_ROUTE } from "features/customers";
import { types as fromHealthSafety, HealthSafetyRoutes } from "features/health-and-safety";
import { ContentBodyProvider } from "frame/providers";
import Header from "./header";
import { SITES_ROUTE, SitesRoutes } from "features/sites";
import { ORGANISATIONS_ROUTE, OrganisationRoutes } from "features/organisations";
import { DOCUMENT_PACK_ROUTE } from "../../features/document-pack/routes";
import DocumentPackRoutes from "../../features/document-pack/components/document-pack-routes";
import {
	ADD_TIMESHEET_ROUTE,
	CONFIRM_TIMESHEET_ROUTE,
	LIST_TIMESHEETS_ROUTE,
	ADD_ADMIN_TIMESHEET_ROUTE,
	ADD_SITE_TIMESHEET_ROUTE,
	ADD_SITE_ADMIN_TIMESHEET_ROUTE,
	LIST_SITE_TIMESHEETS_ROUTE
} from "features/timesheet/timesheet-routes";
import {
	LIST_LEAVES_ROUTE,
	ADD_LEAVE_ROUTE,
	CONFIRM_LEAVE_ROUTE,
	ADD_ADMIN_LEAVE_ROUTE,
	ADD_SITE_ADMIN_LEAVE_ROUTE,
	LIST_SITE_LEAVES_ROUTE,
	NEW_LEAVE_PATH
} from "features/leave/leave-routes";
import { AddTimesheetForm } from "features/timesheet/components/add-timesheet-form";
import { TimesheetList } from "features/timesheet/components/timesheet-list";
import { LeaveList } from "features/leave/components/leave-list";
import NewLeaveForm from "features/leave/components/new-leave-form";
import StaffType from "features/staff-type/staff-type";
import TimesheetReport from 'features/timesheet-report/timesheet-report';
import { TaskTypeListing } from "features/task-type";
import { LeaveTypeListing } from "features/leave-type";
import EquipmentRoutesContainer from "features/equipment/components/equipment-routes-container";
import EquipmentUsageAddPage from "features/equipment/components/equipment-usage-add-page";
import EquipmentUsageListPage from "features/equipment/components/equipment-usage-list-page";
import EquipmentUsageReport from "features/equipment/components/equipment-usage-report";
import {
	EQUIPMENT_USAGE_ADD_ROUTE,
	EQUIPMENT_USAGE_EDIT_ROUTE,
	EQUIPMENT_USAGE_LIST_ROUTE,
} from "features/equipment/actions/types";
import SchedulePage from "features/schedule/components/schedule-page";
import MySchedulePage from "features/schedule/components/my-schedule-page";
import { MY_TASKS_ROUTE, SCHEDULE_ROUTE } from "features/schedule";
import GracePeriodWarning from "features/account/components/subscriptions/grace-period-warning";
import { isStaffUser, isSuperAdminUser } from "utils";

const useStyles = makeStyles(() => ({
	root: {
		height: "100%",
		width: "100%",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden"
	},
	topBar: {
		zIndex: 4, // Higher than navbar (zIndex: 3) to appear above it
		position: "relative"
	},
	container: {
		display: "flex",
		flex: "1 1 auto",
		overflow: "hidden"
	},
	navBar: {
		zIndex: 2, // Lower than top bar (zIndex: 4) so top bar appears above it
		width: 290,
		minWidth: 290,
		flex: "0 0 auto"
	},
	content: {
		overflowY: "auto",
		flex: "1 1 auto"
	}
}));

function getWindowDimensions() {
	const { innerWidth: width, innerHeight: height } = window;
	return {
		width,
		height
	};
}

const DefaultLayout = ({ openMobile, onToggleSideNavBar, role, subscriptionStatus, onFetchSubscriptionStatus }) => {
	const classes = useStyles();
	const ref = useRef();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

	const [originalScreenHeight] = useState(window.innerHeight);
	const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

	useEffect(() => {
		function handleResize() {
			setWindowDimensions(getWindowDimensions());
		}

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Refresh subscription status on layout mount so route guard reflects DB changes
	useEffect(() => {
		if (onFetchSubscriptionStatus) {
			onFetchSubscriptionStatus();
		}
	}, []);

	// Auto-open mobile nav drawer on Home route
	useEffect(() => {
		const path = window.location?.pathname;
		if (isMobile && (path === "/" || path === "/home") && !openMobile) {
			onToggleSideNavBar();
		}
	}, [isMobile, openMobile, onToggleSideNavBar]);

	const isDisabled = subscriptionStatus === "Disabled";
	const isStaff = isStaffUser(role);
	const isSuperAdmin = isSuperAdminUser(role);

	// Disabled staff: show full-screen subscription cancelled message
	if (isDisabled && isStaff) {
		return (
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", padding: 32 }}>
				<Paper elevation={3} style={{ padding: 40, maxWidth: 480, textAlign: "center" }}>
					<Typography variant="h4" gutterBottom style={{ color: "#721c24" }}>
						Subscription Cancelled
					</Typography>
					<Typography variant="body1" gutterBottom>
						Your organisation's subscription has expired or been cancelled.
					</Typography>
					<Typography variant="body1" style={{ marginTop: 16 }}>
						Please contact your account administrator for assistance.
					</Typography>
					<Typography variant="body2" style={{ marginTop: 24, color: "#666" }}>
						<a href="mailto:info@fortherecord.pro">info@fortherecord.pro</a>
					</Typography>
				</Paper>
			</div>
		);
	}

	return (
		<ContentBodyProvider value={ref}>
			{/* Grace period warning popup (shown once per session for admins) */}
			<GracePeriodWarning />
			<div className={classes.root}>
				<Header className={classes.topBar} />
				<div className={classes.container}>
					<NavBar
						className={classes.navBar}
						onToggleSideNavBar={onToggleSideNavBar}
						openMobile={openMobile}
						role={role}
					/>
					<main ref={ref} className={classes.content}>
						{/* Disabled admin: only the subscription/account routes are accessible */}
						{isDisabled && !isSuperAdmin ? (
							<Switch>
								<Route path={ACCOUNT_ROUTE} component={AccountRoutes} />
								<Redirect to={ACCOUNT_SUBSCRIPTION_ROUTE} />
							</Switch>
						) : (
							<Switch>
								<Route path={ACCOUNT_ROUTE} component={AccountRoutes} />
								<Route path="/quick-pic" component={JobsRoutesContainer} />
								<Route path={fromHealthSafety.HEALTH_SAFETY_ROUTE} component={HealthSafetyRoutes} />
								<Route path="/staff" component={StaffRoutesContainer} />
								<Route path="/staff-type" component={StaffType} />
								<Route path="/timesheet-report" component={TimesheetReport} />
								<Route path="/equipment-usage-report" component={EquipmentUsageReport} />
								<Route exact path={EQUIPMENT_USAGE_LIST_ROUTE} component={EquipmentUsageListPage} />
								<Route exact path={EQUIPMENT_USAGE_EDIT_ROUTE} component={EquipmentUsageAddPage} />
								<Route exact path={EQUIPMENT_USAGE_ADD_ROUTE} component={EquipmentUsageAddPage} />
								<Route exact path={SCHEDULE_ROUTE} component={SchedulePage} />
								<Route exact path="/my-schedule">
									<Redirect to={MY_TASKS_ROUTE} />
								</Route>
								<Route exact path={MY_TASKS_ROUTE} component={MySchedulePage} />
								<Route path="/task-type" component={TaskTypeListing} />
								<Route path="/leave-type" component={LeaveTypeListing} />
								<Route path="/equipment" component={EquipmentRoutesContainer} />
								<Route path={CUSTOMERS_ROUTE} component={CustomerRoutes} />
								<Route path={SITES_ROUTE} component={SitesRoutes} />
								<Route path={DOCUMENT_PACK_ROUTE} component={DocumentPackRoutes} />
								<Route exact path={ADD_TIMESHEET_ROUTE} component={AddTimesheetForm} />
								<Route exact path={ADD_SITE_TIMESHEET_ROUTE} component={AddTimesheetForm} />
								<Route exact path={ADD_ADMIN_TIMESHEET_ROUTE} component={AddTimesheetForm} />
								<Route exact path={NEW_LEAVE_PATH} component={NewLeaveForm} />
								<Route exact path={ADD_SITE_ADMIN_TIMESHEET_ROUTE} component={AddTimesheetForm} />
								<Route exact path={LIST_TIMESHEETS_ROUTE} component={TimesheetList} />
								<Route exact path={LIST_SITE_TIMESHEETS_ROUTE} component={TimesheetList} />
								<Route exact path={LIST_LEAVES_ROUTE} component={LeaveList} />
	                            <Route exact path={LIST_SITE_LEAVES_ROUTE} component={LeaveList} />
	                            <Route
	                                path={CONFIRM_TIMESHEET_ROUTE}
	                                render={() => (
	                                    <Redirect to={`${ADD_TIMESHEET_ROUTE}?stopped=1`} />
	                                )}
	                            />
								<Route path={ORGANISATIONS_ROUTE} component={OrganisationRoutes} />
								<Route path={INFO_HOME_ROUTE} component={InfoRoutes} />
								<Route path={TASK_HOME_ROUTE} component={TaskRoutes} />
								<Route
									path={SUPER_ADMIN_DASHBOARD_ROUTE}
									component={SuperAdminDashboard}
								/>
								<Route path={ADMIN_HOME_ROUTE} component={AdminHome} />
								<Route path={HOME_ROUTE} component={HomeRoutes} />
							</Switch>
						)}
					</main>
				</div>
			</div>
		</ContentBodyProvider>
	);
};

DefaultLayout.propTypes = {
    openMobile: PropTypes.bool.isRequired,
    onToggleSideNavBar: PropTypes.func.isRequired,
    role: PropTypes.string,
    subscriptionStatus: PropTypes.string,
    onFetchSubscriptionStatus: PropTypes.func,
};

DefaultLayout.defaultProps = {
    role: "",
    subscriptionStatus: "Active",
};

export default DefaultLayout;
